import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { YDivider } from 'irmlib/styles'
import { closeInformationDialog } from 'irmlib/containers'
import { useForceRender, useConstructor } from 'irmlib/hooks'
import { doLogout } from '../../../../common/functions'
import { useTranslation } from 'react-i18next'

const SessionTimeoutDialogBodyContainer = styled.div`
  width: 400px;
`

let isTesting = false
if (process.env.NODE_ENV === 'production') {
  isTesting = false
}

// 1 second
const defaultMonitorSec = 1

// 7 minutes
let defaultExpirationSec = 7 * 60
if (isTesting) {
  defaultExpirationSec = 20
}

let defaultWarningExpirationSec = 30
if (isTesting) {
  defaultWarningExpirationSec = 20
}

const SessionTimeoutWrapper = forwardRef((props, ref) => {
  const idleTime = useRef(0) 
  const timeoutId = useRef(null)
  const timerOff = useRef(false)

  useImperativeHandle(ref, () => ({
    restartTimer: () => {
      timerOff.current = false
      idleTime.current = 0
    }
  }))

  const handleTimeout = () => {
    if (timerOff.current) {
      return
    }
    idleTime.current += defaultMonitorSec
    if (isTesting) {
      // eslint-disable-next-line no-console
      console.log('handleTimeout', idleTime.current, defaultExpirationSec)
    }
    if (idleTime.current >= defaultExpirationSec) {
      timerOff.current = true
      idleTime.current = 0
      if (isTesting) {
        // eslint-disable-next-line no-console
        console.log('onExpired')
      }
      props.onExpired && props.onExpired()
    }
  }

  useEffect(() => {
    if (isTesting) {
      // eslint-disable-next-line no-console
      console.log('SessionTimeoutWrapper constructed')
    }
    timeoutId.current = setInterval(handleTimeout, defaultMonitorSec * 1000)
    return () => {
      if (isTesting) {
        // eslint-disable-next-line no-console
        console.log('SessionTimeoutWrapper destoyed')
      }
      if (timeoutId.current !== null) {
        clearInterval(timeoutId.current)
        timeoutId.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (props.isLoggedIn) {
      timerOff.current = false
      idleTime.current = 0
    } else {
      timerOff.current = true
      idleTime.current = 0
    }
  }, [props.isLoggedIn])

  const handleOnAction = event => {
    if (isTesting) {
      // eslint-disable-next-line no-console
      console.log('handleOnAction', event)
    }
    idleTime.current = 0
  }

  useIdleTimer({
    onAction: handleOnAction
  })

  return (
    <>
      {props.children}
    </>
  )
})

SessionTimeoutWrapper.propTypes = {
  // Call this function when the idle timer expires.
  onExpired: PropTypes.func,
  // Child component(s) to be wrapped with this monitor component.
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  isLoggedIn: PropTypes.bool.isRequired
}

export default SessionTimeoutWrapper

export const SessionTimeoutDialogBody = props => {
  const forceRender = useForceRender()
  const idleTime = useRef(0) 
  const timeoutId = useRef(null)
  const message1 = useRef('')
  const message2 = useRef('')
  const { t } = useTranslation()

  const handleTimeout = (isConstructor) => {
    idleTime.current += 1
    if (isTesting) {
      // eslint-disable-next-line no-console
      console.log('Countdown to logout', idleTime.current, defaultWarningExpirationSec, isConstructor)
    }
    if (idleTime.current >= defaultWarningExpirationSec && !isConstructor) {
      // remove this dialog and then logout
      closeInformationDialog()
      doLogout()
    } else {
      const totalIdleSeconds = defaultExpirationSec + idleTime.current
      const minutes = parseInt(totalIdleSeconds / 60)
      const seconds = parseInt(totalIdleSeconds % 60)

      // Update ui
      const secondsRemaining = defaultWarningExpirationSec - idleTime.current
      message1.current = `${t('app.totalIdleTime')} ${minutes} ${t('app.minutes')} ${seconds} ${t('app.seconds')}.`
      message2.current = `${t('app.secondsRemaining')} ${secondsRemaining}.`
      if (!isConstructor) {
        forceRender()
      }
    }
  }

  useConstructor(() => {
    handleTimeout(true)
  })

  useEffect(() => {
    if (isTesting) {
      // eslint-disable-next-line no-console
      console.log('SessionTimeoutDialogBody constructed')
    }
    timeoutId.current = setInterval(handleTimeout, 1000)
    return () => {
      if (isTesting) {
        // eslint-disable-next-line no-console
        console.log('SessionTimeoutDialogBody destoyed')
      }
      if (timeoutId.current !== null) {
        clearInterval(timeoutId.current)
        timeoutId.current = null
      }
    }
  }, [])

  return (
    <SessionTimeoutDialogBodyContainer>
      <div>
        {message1.current}
      </div>
      <YDivider height='20px' />
      <div>
        {message2.current}
      </div>
    </SessionTimeoutDialogBodyContainer>
  )
}
